import { createRef, Component, forwardRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { trans } from '@spotahome/soyuz/client';

import {
  getComponentToRender,
  getLiteralToDisplay
} from './utils/getSliderComponent';
import {
  getNextItemIndex,
  getPreviousItemIndex
} from './utils/sliderIndexCalculator';
import { ITEM_WIDTH, LIMIT_TO_SWIPE, KEYS } from './utils/constants';
import {
  hasEndedSwipeAndMustStay,
  hasEndedSwipeAndMustContinue,
  hasEndedNavigation
} from './utils/sliderConditions';

import GalleryArrow from './GalleryArrow';

import './SliderTrack.scss';

const USE_CURRENT_INDEX = true;

const getInitialState = (currentIndex = 0) => ({
  enteringItemIndex: null,
  enteringItemPosition: null,
  position: 0,
  isTransitioning: false,
  isSwiping: false,
  currentIndex
});

const getEnteringItem = (index, position) => ({
  enteringItemIndex: index,
  enteringItemPosition: position
});

class SliderTrack extends Component {
  currentImageRef = createRef();

  static propTypes = {
    type: PropTypes.oneOf(['img', 'plan', 'video', 'map']).isRequired,
    items: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    currentTrackItem: PropTypes.number.isRequired
  };

  state = getInitialState(this.props.currentTrackItem);

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyboardPress);
  }

  UNSAFE_componentWillReceiveProps = () => {
    this.setState(getInitialState(this.props.currentTrackItem));
  };

  componentDidUpdate = (_, prevState) => {
    if (hasEndedSwipeAndMustStay(prevState, this.state)) {
      this.resetInitialStateWithIndex(USE_CURRENT_INDEX);
    }

    if (hasEndedSwipeAndMustContinue(prevState, this.state)) {
      this.resetInitialStateWithIndex();
    }

    if (hasEndedNavigation(prevState, this.state)) {
      this.updatePositions();
    }
  };

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyboardPress);
  }

  handleKeyboardPress = e => {
    if (e.keyCode === KEYS.NEXT_ARROW) {
      this.handleNextItem(e);
    } else if (e.keyCode === KEYS.PREV_ARROW) {
      this.handlePreviousItem(e);
    }
  };

  handleEndTransition = () => {
    this.setState({ isTransitioning: false });
  };

  handleNextItem = e => {
    e && e.preventDefault();
    this.setState(this.getNextEnteringItem);
  };

  handlePreviousItem = e => {
    e && e.preventDefault();
    this.setState(this.getPrevEnteringItem);
  };

  handleTouchStart = e => {
    this.initialPosition = this.getItemPosition() - e.touches[0].clientX;
  };

  handleTouchMove = e => {
    const touchMove = this.initialPosition + e.touches[0].clientX;
    const itemWidth = this.getItemWidth();
    let enteringItemInfo = {};
    let newState = {
      isSwiping: true,
      isTransitioning: false,
      position: (touchMove * ITEM_WIDTH) / itemWidth
    };

    if (newState.position > ITEM_WIDTH) {
      this.initialPosition -= itemWidth;
      newState = {
        ...newState,
        position: newState.position - ITEM_WIDTH,
        currentIndex: this.state.enteringItemIndex
      };
      enteringItemInfo = this.getPrevEnteringItem(newState);
    } else if (newState.position < -ITEM_WIDTH) {
      this.initialPosition += itemWidth;
      newState = {
        ...newState,
        position: newState.position + ITEM_WIDTH,
        currentIndex: this.state.enteringItemIndex
      };
      enteringItemInfo = this.getNextEnteringItem(newState);
    } else if (newState.position > 0) {
      enteringItemInfo = this.getPrevEnteringItem(this.state);
    } else if (newState.position < 0) {
      enteringItemInfo = this.getNextEnteringItem(this.state);
    }

    this.setState({
      ...newState,
      ...enteringItemInfo
    });
  };

  handleTouchEnd = () => {
    if (Math.abs(this.state.position) >= LIMIT_TO_SWIPE) {
      this.updatePositions();
    } else {
      this.setState({
        position: 0,
        isTransitioning: true
      });
    }
  };

  getItemWidth = () => this.currentImageRef.current.clientWidth;

  getItemPosition = () => this.currentImageRef.current.offsetLeft;

  getNextEnteringItem = prevState => {
    const nextIndex = getNextItemIndex(
      prevState.currentIndex,
      this.props.items.length
    );

    return getEnteringItem(nextIndex, ITEM_WIDTH);
  };

  getPrevEnteringItem = prevState => {
    const prevIndex = getPreviousItemIndex(
      prevState.currentIndex,
      this.props.items.length
    );

    return getEnteringItem(prevIndex, -ITEM_WIDTH);
  };

  getItemStyle = left => ({
    left: `${left}%`
  });

  resetInitialStateWithIndex = useCurrent => {
    this.setState(prevState => {
      const indexToUse = useCurrent
        ? prevState.currentIndex
        : prevState.enteringItemIndex;
      return getInitialState(indexToUse);
    });
  };

  updatePositions = () => {
    this.setState(prevState => ({
      position: -1 * prevState.enteringItemPosition,
      isTransitioning: true,
      isSwiping: false
    }));
  };

  isIdle = () => !this.state.isTransitioning;

  renderEnteringItem = ComponentToRender =>
    this.state.enteringItemIndex !== null && (
      <span
        className="slider-track__entering-item"
        style={this.getItemStyle(this.state.enteringItemPosition)}
      >
        <ComponentToRender
          key="enteringItem"
          item={this.props.items[this.state.enteringItemIndex]}
        />
      </span>
    );

  render() {
    const ComponentToRender = getComponentToRender(this.props.type);
    const currentItem = this.props.items[this.state.currentIndex];

    const trackClassName = classNames('slider-track__track', {
      'slider-track__track--animating': !this.isIdle()
    });

    return (
      <Fragment>
        <div className="slider-track__wrap">
          <GalleryArrow navRole="prev" onClick={this.handlePreviousItem} />
          <div
            ref={this.currentImageRef}
            className={trackClassName}
            onTransitionEnd={this.handleEndTransition}
            onTouchStart={this.handleTouchStart}
            onTouchMove={this.handleTouchMove}
            onTouchEnd={this.handleTouchEnd}
            style={this.getItemStyle(this.state.position)}
          >
            <ComponentToRender key="currentItem" item={currentItem} />
            {this.renderEnteringItem(ComponentToRender)}
          </div>
          <GalleryArrow navRole="next" onClick={this.handleNextItem} />
        </div>
        <div ref={this.props.forwardedRef} className="slider-track__counter">
          {this.state.currentIndex + 1}/{this.props.items.length}{' '}
          {trans(getLiteralToDisplay(this.props.type))}
        </div>
      </Fragment>
    );
  }
}

SliderTrack.propTypes = {
  type: PropTypes.oneOf(['img', 'plan', 'video', 'map']).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.object })
  ]).isRequired
};

export default forwardRef((props, ref) => (
  <SliderTrack {...props} forwardedRef={ref} />
));
